<template lang="pug">
  //- -------------------------------------------------------------------------
  //- NOTE: props.challenge needs to have the teams relation and team.isMember
  //- team.isAdmin attributes
  //- -------------------------------------------------------------------------
  div(v-if="!isAdmin && isTeamAdmin && !isJoined && allMemberRelatedTeams.length > 0")
    WtJoinButton(
      teamJoinText="Add My Team"
      :key="allMemberRelatedTeams[0].id"
      :challenge="challenge" :team="allMemberRelatedTeams[0]"
      @isMemberParticipating="$event => $set(teamsJoinedStatus, allMemberRelatedTeams[0].id, $event)"
    )
  b-autocomplete(
    v-else-if="isAdmin"
    :data="teamAutocomplete"
    :disabled="!(isTeamAdmin || isAdmin)"
    :loading="isSearching"
    :open-on-focus="true"
    :clear-on-select="true"
    @focus="autocompleteSearch"
    @typing="autocompleteSearch"
    @select="autocompleteSelect"
    placeholder="Add Team"
    max-height="300px"
    dropdown-position="bottom"
  )
    template(v-slot="props")
      article.media
        figure.media-left
          p.image.is-48x48
            img.is-rounded(
              :src="_.get(props.option, 'logo', 'https://cdn.tbybewell.com/images/default-avatar.png')"
              alt="avatar"
              @error="teamAvatarOnError"
            )
        .media-content
          .content
            .has-text-weight-semibold {{ props.option.name }}
            .is-5 {{ adminMemberText(props.option) }}
  BTooltip.custom-tool-tip(
    v-else
    :active="isTooltipActive"
    multilined
    :label="tooltipText"
    :position="$route.matched[0].path === \"/challenges/:challengeId\" ? 'is-right' :  'is-top'"
    animated
    slot="trigger"
  )
    b-button.wt-join-button(disabled) {{ joinWithText }}
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import WtButton from "@@/WtButton/WtButton";
import WtJoinButton from "@@/WtJoinButton/WtJoinButton";
import debounce from "lodash/debounce";

export default {
  name: "JoinChallengeWithTeam",

  components: {
    WtButton,
    WtJoinButton
  },

  props: {
    challenge: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      teamsJoinedStatus: {},
      teamAutocomplete: [],
      isSearching: false
    };
  },
  computed: {
    ...mapGetters("authMember", ["isAdmin"]),
    ...mapGetters("members", [
      "memberTeams",
      "memberTeamsLoaded",
      "teamsMemberIsAdmin",
      "teamsMemberIsAdminLoaded"
    ]),

    allMemberRelatedTeams() {
      if (this.isAdmin) {
        return [];
      }
      return this._.uniqBy(
        [
          ...this.challenge.teams.filter(team => team.isMember),
          ...this.teamsMemberIsAdmin
        ],
        "id"
      );
    },

    isJoined() {
      return this.allMemberRelatedTeams.some(
        team => this.teamsJoinedStatus[team.id]
      );
    },

    isTeamAdmin() {
      // is admin of at least one team
      if (this.isAdmin) {
        return true;
      }
      return this.teamsMemberIsAdmin.length > 0;
    },

    awaitingTeamLeaderToJoin() {
      return !this.isTeamAdmin && this.memberTeams.length > 0 && !this.isJoined;
    },

    noTeam() {
      return this.memberTeams.length < 1;
    },

    tooltipText() {
      if (
        !(this.isTeamAdmin || this.isAdmin) &&
        (this.noTeam || this.awaitingTeamLeaderToJoin)
      ) {
        return "If you are already on a team, ask your team lead to add your team to this challenge.  If you are not on a team yet, create your own (if applicable to your program),  or join a participating team!";
      }
      return "";
    },

    isTooltipActive() {
      if (this.isAdmin) {
        return false;
      }
      return this.awaitingTeamLeaderToJoin || this.noTeam;
    },

    joinWithText() {
      if (this.isAdmin) {
        return "Add A Team";
      }
      if (!this.isJoined && this.isTeamAdmin) {
        return "Add A Team";
      }
      if (this.isJoined) {
        return "Joined"; // disabled with green-check
      }
      return "How to Join";
    }
  },

  async mounted() {
    if (!this.teamsMemberIsAdminLoaded) {
      await this.fetchTeamsMemberIsAdmin(this.$auth.user().id);
    }
  },

  methods: {
    ...mapActions("challenges", ["join"]),
    ...mapActions("members", ["fetchTeamsMemberIsAdmin"]),
    ...mapActions("program", ["searchProgramTeams"]),

    adminMemberText(team) {
      // you're either a member or an admin or both but never neither in this list: allMemberRelatedTeams
      if (team.isAdmin && team.isMember) {
        return ""; // "* Team Leader And Participant In Challenges";
      }
      if (team.isAdmin && !team.isMember) {
        return ""; // "* Team Leader But Not Participant In Challenges";
      }
      if (!team.isAdmin && team.isMember) {
        return ""; // "* Team Member";
      }
      return "";
    },

    teamAvatarOnError(event) {
      event.target.onerror = null;
      event.target.src = "https://cdn.tbybewell.com/images/default-avatar.png";
    },
    async teamSearch(search) {
      if (typeof search !== "string") {
        search = "";
      }

      this.isSearching = true;

      this.searchProgramTeams({
        programId: this.$auth.user().programId,
        searchTerm: search,
        sortColumn: null,
        sortOrder: null,
        notInChallengeId: this.challenge.id,
        perPage: 10
      })
        .then(response => {
          this.isSearching = false;

          if (response.status === 200) {
            this.teamAutocomplete = response.data;
          } else {
            this.$buefy.toast.open("Failed to upload deletion feed.");
          }
        })
        .catch(e => {
          this.isSearching = false;

          this.$buefy.toast.open("Failed to load team list.");

          console.error(e);
        });
    },
    async joinChallenge(teamEntity) {
      let payload = {
        challenge: this.challenge,
        team: teamEntity
      };

      await this.join(payload);
    },
    autocompleteSelect(item) {
      this.joinChallenge(item);
    },
    autocompleteSearch: debounce(async function(search) {
      await this.teamSearch(search);
    }, 500)
  }
};
</script>
